import React from "react";
import {
  TopicRelateAnswerProviderProps,
  TopicRelateAnswerValue,
} from "./TopicRelateAnswer.type";
import { CharacterResult } from "common/character/dto/CharacterResult";
import { CharacterSearchParams } from "common/api/asocijator/character/type/character_api.type";
import { useCharacterSearch } from "hooks/asocijator/character/useCharacterSearch";
import { CHARACTER_RELATE_CONTAIN } from "components/common/character/CharacterRelateContain/CharacterRelateContainNav/CharacterRelateContainNav.const";
import {
  answerRelateTypeByLetterGet,
  answerRelateTypeGet,
  charContainByLetter,
  topicRelateActionDefaultGet,
  topicRelateSelectorDefaultGet,
} from "./TopicRelateAnswer.logic";
import { RELATE_ANSWER } from "./TopicRelateAnswer.const";
import { characterByLetter } from "./TopicRelateAnswer.logic";
import { filterCharactersByLetters } from "common/character/character_common";

const TopicRelateAnswer = React.createContext<TopicRelateAnswerValue>({
  characters: [],
  topicCharacters: [],
  topicAnswerCharacters: [],
  selector: topicRelateSelectorDefaultGet(),
  action: topicRelateActionDefaultGet(),
});

export function TopicRelateAnswerProvider({
  children,
  topicId,
}: TopicRelateAnswerProviderProps) {
  const [characters, setCharacters] = React.useState<CharacterResult[]>([]);
  const searchParams = React.useMemo<CharacterSearchParams>(
    () => ({
      limit: 0,
      skip: 0,
      topicId,
      searchType: CHARACTER_RELATE_CONTAIN.CONTAIN,
      recursive: true,
    }),
    [topicId]
  );
  const characterListResult = useCharacterSearch(searchParams);
  const topicCharacters = React.useMemo<CharacterResult[]>(
    () =>
      characters.filter(
        (character: CharacterResult) => character.topicId === topicId
      ),
    [characters]
  );
  const topicAnswerCharacters = React.useMemo<CharacterResult[]>(
    () => filterCharactersByLetters(topicCharacters),
    [topicCharacters]
  );

  React.useEffect(() => {
    if (characterListResult) {
      setCharacters(characterListResult.items);
    }
  }, [characterListResult]);

  const byLetter = React.useCallback(
    (letter: string) => characterByLetter(letter, characters),
    [characters]
  );

  const answerRelateTypeByLetter = React.useCallback(
    (letter: string) => answerRelateTypeByLetterGet(letter, characters),
    [characters]
  );

  const containByLetter = React.useCallback(
    (letter: string) => charContainByLetter(letter, characters),
    [characters]
  );

  const answerSet = (letter: string, correct: boolean) => {
    const character = byLetter(letter);
    if (character) {
      if (!character.answer) {
        character.answer = {};
      }
      character.answer.correct = correct ? "y" : "n";
      setCharacters([...characters]);
    }
  };

  return (
    <TopicRelateAnswer.Provider
      value={{
        characters,
        topicCharacters,
        topicAnswerCharacters,
        selector: { byLetter, answerRelateTypeByLetter, containByLetter },
        action: { answerSet },
      }}
    >
      {children}
    </TopicRelateAnswer.Provider>
  );
}

export function useTopicRelateAnswer() {
  return React.useContext(TopicRelateAnswer);
}
